/**
 * Gastos compartidos.
 *
 * La pantalla está pensada para responder a una sola pregunta: **quién me debe y
 * desde cuándo**. Por eso lo primero que se ve son los deudores con lo más viejo
 * que tienen pendiente, que es lo que hay que cobrar.
 *
 * Y explica la sutileza que más cuesta entender: si pagas 100.000 de una cena
 * entre cuatro, de tu cuenta salen 100.000 pero tu gasto son 25.000.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HandCoins, MessageCircle, Plus, Users } from 'lucide-react';
import { useState } from 'react';

import { daysBetweenISO, formatCOP, formatDateLabel, parseAmountFromText, toLocalDateISO, todayISO, toLocalTimeHHmm } from '@eldescuadre/core';
import type { Split } from '@eldescuadre/contracts';

import {
  createSplit,
  deleteSplit,
  getAccounts,
  getCategories,
  getReceivables,
  getSplits,
  settleSplit,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Progress, Stat, SubmitButton } from '../components/ui';

export function SplitsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [settling, setSettling] = useState<Split | null>(null);
  const [onlyPending, setOnlyPending] = useState(true);

  const splits = useQuery({ queryKey: ['splits'], queryFn: getSplits });
  const receivables = useQuery({ queryKey: ['splits', 'receivables'], queryFn: getReceivables });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['splits'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
      queryClient.invalidateQueries({ queryKey: ['reports'] }),
    ]);
  };

  const remove = useMutation({ mutationFn: deleteSplit, onSuccess: refresh });

  const all = splits.data?.splits ?? [];
  const visible = onlyPending ? all.filter((s) => s.status !== 'SETTLED') : all;

  /** Enlace de WhatsApp con el recordatorio ya escrito, para no redactarlo. */
  const reminderLink = (split: Split, debtor: { amount: number; oldestDate: string | null }): string | null => {
    const contact = split.debtorContact?.replace(/\D/g, '');
    if (!contact) return null;

    const days = debtor.oldestDate ? daysBetweenISO(debtor.oldestDate, todayISO()) : null;
    const message =
      `Hola${split.debtorName ? ` ${split.debtorName}` : ''}, te recuerdo lo del ` +
      `${split.description.toLowerCase()}: me quedaste debiendo ${formatCOP(debtor.amount)}` +
      `${days !== null && days > 0 ? ` desde hace ${days} días` : ''}. ` +
      'Cuando puedas, sin afán.';

    return `https://wa.me/${contact}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Compartidos</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">Quién te debe y desde cuándo.</p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Dividir
        </button>
      </header>

      {receivables.isLoading && <Loading label="Sumando lo que te deben…" />}

      {receivables.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Te deben"
              value={<Money amount={receivables.data.totalReceivable} />}
              hint={`${receivables.data.pendingCount} ${receivables.data.pendingCount === 1 ? 'gasto pendiente' : 'gastos pendientes'}`}
              tone="gold"
            />
            <Stat
              label="Ya te han pagado"
              value={<Money amount={receivables.data.totalSettled} />}
              hint="De gastos compartidos anteriores"
              tone="positive"
            />
          </div>

          {/* Quién me debe, ordenado por lo que más pesa. */}
          {receivables.data.byDebtor.length > 0 && (
            <Card title="Por persona">
              <ul className="space-y-3">
                {receivables.data.byDebtor.map((debtor) => {
                  const split = all.find((s) => (s.debtorName ?? 'Sin nombre') === debtor.debtorName);
                  const link = split ? reminderLink(split, debtor) : null;
                  const days = debtor.oldestDate ? daysBetweenISO(debtor.oldestDate, todayISO()) : null;

                  return (
                    <li key={debtor.debtorName}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 font-medium">
                            <Users size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                            <span className="truncate">{debtor.debtorName}</span>
                          </p>
                          <p className="mt-0.5 text-xs text-ed-text-muted">
                            {debtor.count} {debtor.count === 1 ? 'gasto' : 'gastos'}
                            {days !== null && days > 0 && (
                              <>
                                {' · '}
                                <span className={days > 30 ? 'text-ed-coral-light' : ''}>
                                  el más viejo, hace {days} días
                                </span>
                              </>
                            )}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <Money amount={debtor.amount} className="font-semibold" />
                          {link && (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ed-btn-ghost !min-h-[36px] !px-2"
                              aria-label={`Recordarle a ${debtor.debtorName} por WhatsApp`}
                              title="Recordarle por WhatsApp"
                            >
                              <MessageCircle size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-ed-text-muted">
                El recordatorio se escribe solo con el monto y los días. Tú decides si lo envías.
              </p>
            </Card>
          )}
        </>
      )}

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ed-bg-border"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
          />
          Solo los que están pendientes
        </label>
      </div>

      {splits.isLoading && <Loading />}

      {splits.data && visible.length === 0 && (
        <Card>
          <EmptyState
            title={onlyPending ? 'Nadie te debe nada' : 'Sin gastos compartidos'}
            message={
              onlyPending
                ? 'Cuando dividas un gasto y alguien quede debiéndote, aparecerá aquí.'
                : 'Divide una cuenta entre varios y la aplicación lleva la cuenta de quién te debe.'
            }
            action={
              <button
                type="button"
                className="ed-btn-primary !w-auto !px-4"
                onClick={() => setCreating(true)}
              >
                Dividir un gasto
              </button>
            }
          />
        </Card>
      )}

      {visible.length > 0 && (
        <Card title="Gastos compartidos">
          <ul className="space-y-3">
            {visible.map((split) => {
              const pending = split.receivable - split.settledAmount;
              const isSettled = split.status === 'SETTLED';

              return (
                <li
                  key={split.id}
                  className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium">
                        <HandCoins size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                        <span className="truncate">{split.description}</span>
                        {isSettled && (
                          <CheckCircle2 size={14} className="shrink-0 text-ed-success" aria-hidden="true" />
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-ed-text-muted">
                        Total {formatCOP(split.total)} · tu parte {formatCOP(split.myShare)}
                        {split.participants > 1 && <> · {split.participants} personas</>}
                        {' · '}
                        {formatDateLabel(toLocalDateISO(new Date(split.occurredAt)))}
                      </p>
                      {split.debtorName && (
                        <p className="mt-0.5 text-xs text-ed-text-muted">
                          Le corresponde a {split.debtorName}
                        </p>
                      )}
                    </div>

                    <div className="shrink-0 text-right">
                      {isSettled ? (
                        <Money amount={0} className="block font-semibold text-ed-success" />
                      ) : (
                        <Money amount={pending} className="block font-semibold text-ed-gold" />
                      )}
                      <span className="block text-xs text-ed-text-muted">
                        {isSettled ? 'saldado' : 'pendiente'}
                      </span>
                    </div>
                  </div>

                  {split.status === 'PARTIAL' && (
                    <div className="mt-2">
                      <Progress
                        percent={
                          split.receivable > 0 ? (split.settledAmount / split.receivable) * 100 : 0
                        }
                        tone="cyan"
                        label={`Cobrado: ${Math.round((split.settledAmount / split.receivable) * 100)} por ciento`}
                      />
                      <p className="mt-1 text-xs text-ed-text-muted">
                        Ya te pagaron <Money amount={split.settledAmount} /> de{' '}
                        <Money amount={split.receivable} />.
                      </p>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {!isSettled && (
                      <button
                        type="button"
                        className="ed-btn-secondary !min-h-[40px] flex-1 text-xs"
                        onClick={() => setSettling(split)}
                      >
                        Me pagaron
                      </button>
                    )}
                    <button
                      type="button"
                      className="ed-btn-ghost !min-h-[40px] !w-auto !px-3 text-xs text-ed-coral-light"
                      onClick={() => {
                        if (window.confirm('¿Eliminar este gasto compartido? El gasto volverá a contar completo.')) {
                          remove.mutate(split.id);
                        }
                      }}
                      aria-label={`Eliminar ${split.description}`}
                    >
                      Eliminar
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <Note>
        Cuando pagas <strong>100.000</strong> de una cena entre cuatro, de tu cuenta salen los
        100.000, pero tu gasto son <strong>25.000</strong>. Los otros 75.000 son dinero que te
        deben, no un gasto. Así los informes no mienten.
      </Note>

      {creating && (
        <NewSplitModal
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}

      {settling && (
        <SettleModal
          split={settling}
          onClose={() => setSettling(null)}
          onSaved={async () => {
            setSettling(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Dividir un gasto nuevo. */
function NewSplitModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [description, setDescription] = useState('');
  const [totalText, setTotalText] = useState('');
  const [participants, setParticipants] = useState('2');
  const [myShareText, setMyShareText] = useState('');
  const [debtorName, setDebtorName] = useState('');
  const [debtorContact, setDebtorContact] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const total = parseAmountFromText(totalText) ?? 0;
  const people = Number(participants) || 2;

  // Se propone la parte equitativa, pero se puede cambiar: a veces no es igual.
  const suggestedShare = Math.round(total / people);
  const myShare = myShareText.trim() ? (parseAmountFromText(myShareText) ?? 0) : suggestedShare;
  const receivable = Math.max(0, total - myShare);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => getCategories('EXPENSE'),
  });

  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';
  const problem =
    total <= 0
      ? 'Escribe cuánto pagaste.'
      : myShare > total
        ? 'Tu parte no puede ser mayor que el total.'
        : !chosenAccountId
          ? 'Elige de qué cuenta salió el dinero.'
          : null;

  const save = useMutation({
    mutationFn: () =>
      createSplit({
        description: description.trim() || 'Gasto compartido',
        total,
        myShare,
        participants: people,
        date: todayISO(),
        time: toLocalTimeHHmm(new Date()),
        accountId: chosenAccountId,
        categoryId: categoryId || undefined,
        debtorName: debtorName.trim() || undefined,
        debtorContact: debtorContact.trim() || undefined,
        reimbursable: true,
        isCompanyExpense: false,
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Dividir un gasto"
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={problem !== null}
          onClick={() => save.mutate()}
        >
          Guardar
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="¿Qué fue?" htmlFor="descripcion-split">
          <input
            id="descripcion-split"
            className="ed-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Cena del viernes"
            autoFocus
            autoComplete="off"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="¿Cuánto pagaste en total?" htmlFor="total-split">
            <input
              id="total-split"
              className="ed-input ed-money"
              inputMode="numeric"
              value={totalText}
              onChange={(e) => setTotalText(e.target.value)}
              placeholder="100.000"
              autoComplete="off"
            />
          </Field>

          <Field label="¿Entre cuántos?" htmlFor="personas-split">
            <input
              id="personas-split"
              className="ed-input"
              inputMode="numeric"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
              placeholder="4"
              autoComplete="off"
            />
          </Field>
        </div>

        <Field
          label="Tu parte"
          htmlFor="mi-parte"
          hint={`Si lo reparten en partes iguales, te toca ${formatCOP(suggestedShare)}.`}
        >
          <input
            id="mi-parte"
            className="ed-input ed-money"
            inputMode="numeric"
            value={myShareText}
            onChange={(e) => setMyShareText(e.target.value)}
            placeholder={String(suggestedShare)}
            autoComplete="off"
          />
        </Field>

        {total > 0 && (
          <div className="space-y-1.5 rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Sale de tu cuenta</span>
              <Money amount={total} className="font-semibold" />
            </div>
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Tu gasto real</span>
              <Money amount={myShare} className="font-semibold text-ed-success" />
            </div>
            <div className="flex justify-between border-t border-ed-bg-border/30 pt-2">
              <span className="text-ed-text-muted">Te deben</span>
              <Money amount={receivable} className="font-semibold text-ed-gold" />
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="¿Quién te debe? (opcional)" htmlFor="deudor">
            <input
              id="deudor"
              className="ed-input"
              value={debtorName}
              onChange={(e) => setDebtorName(e.target.value)}
              placeholder="Carlos"
              autoComplete="off"
            />
          </Field>

          <Field
            label="Teléfono (opcional)"
            htmlFor="contacto"
            hint="Para poder recordarle con un toque."
          >
            <input
              id="contacto"
              className="ed-input"
              type="tel"
              inputMode="tel"
              value={debtorContact}
              onChange={(e) => setDebtorContact(e.target.value)}
              placeholder="300 123 4567"
              autoComplete="off"
            />
          </Field>
        </div>

        <Field label="Categoría" htmlFor="categoria-split">
          <select
            id="categoria-split"
            className="ed-input"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="¿De qué cuenta salió?" htmlFor="cuenta-split">
          <select
            id="cuenta-split"
            className="ed-input"
            value={chosenAccountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Elige una cuenta…</option>
            {(accounts.data?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} · {formatCOP(account.balance)}
              </option>
            ))}
          </select>
        </Field>

        {problem && <ErrorNote message={problem} />}
        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/** Registrar que te pagaron. */
function SettleModal({
  split,
  onClose,
  onSaved,
}: {
  split: Split;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const pending = split.receivable - split.settledAmount;
  const [amountText, setAmountText] = useState(String(pending));
  const [accountId, setAccountId] = useState('');

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const amount = parseAmountFromText(amountText) ?? 0;
  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  const problem =
    amount <= 0
      ? 'Escribe cuánto te pagaron.'
      : amount > pending
        ? `Solo te deben ${formatCOP(pending)} de este gasto.`
        : null;

  const save = useMutation({
    mutationFn: () =>
      settleSplit(split.id, {
        amount,
        date: todayISO(),
        time: toLocalTimeHHmm(new Date()),
        accountId: chosenAccountId,
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Me pagaron"
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={problem !== null || !chosenAccountId}
          onClick={() => save.mutate()}
        >
          Registrar el cobro
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <p className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 px-3 py-2 text-sm">
          Te deben <Money amount={pending} className="font-semibold" /> de «{split.description}».
        </p>

        <Field label="¿Cuánto te pagaron?" htmlFor="monto-cobro">
          <input
            id="monto-cobro"
            className="ed-input ed-money"
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            autoFocus
            autoComplete="off"
          />
        </Field>

        <div className="flex gap-2">
          <button
            type="button"
            className="ed-btn-secondary !min-h-[36px] !w-auto !px-3 text-xs"
            onClick={() => setAmountText(String(pending))}
          >
            Todo ({formatCOP(pending)})
          </button>
          <button
            type="button"
            className="ed-btn-secondary !min-h-[36px] !w-auto !px-3 text-xs"
            onClick={() => setAmountText(String(Math.round(pending / 2)))}
          >
            La mitad
          </button>
        </div>

        <Field label="¿A qué cuenta entró?" htmlFor="cuenta-cobro">
          <select
            id="cuenta-cobro"
            className="ed-input"
            value={chosenAccountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Elige una cuenta…</option>
            {(accounts.data?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} · {formatCOP(account.balance)}
              </option>
            ))}
          </select>
        </Field>

        <Note>
          Este cobro <strong>no cuenta como ingreso nuevo</strong>. Es dinero tuyo que vuelve, no
          dinero que ganas. Si lo contáramos como ingreso, tus informes inflarían lo que ganas.
        </Note>

        {problem && <ErrorNote message={problem} />}
        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}
